import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import {
  formatDisplayDate,
  formatIsoDate,
  inclusiveDayCount,
  isValidCustomDateRange,
  MAX_CUSTOM_DATE_RANGE_DAYS,
  parseIsoDate,
} from '../../utils/resolveDateRangePreset';
import { Button } from '@/lib';

type DateRangeCustomPanelProps = {
  initialFrom?: string;
  initialTo?: string;
  onApply: (range: { from: string; to: string }) => void;
  onCancel: () => void;
};

const DOW_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const sameDay = (a: Date | null, b: Date | null) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const buildMonthCells = (month: Date, today: Date, rangeStart: Date | null, rangeEnd: Date | null, hoverDay: Date | null) => {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1);
  const offset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const end = rangeEnd || hoverDay;
  const lo = rangeStart && end ? (rangeStart <= end ? rangeStart : end) : null;
  const hi = rangeStart && end ? (rangeStart <= end ? end : rangeStart) : null;

  const cells: Array<{ key: string; day?: number; date?: Date; className: string }> = [];

  for (let i = 0; i < offset; i += 1) {
    cells.push({ key: `empty-${year}-${monthIndex}-${i}`, className: 'ar-date__day empty' });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, monthIndex, day);
    const classNames = ['ar-date__day'];

    if (date > today) {
      classNames.push('muted');
    }
    if (sameDay(date, today)) {
      classNames.push('today');
    }

    if (lo && hi) {
      if (date > lo && date < hi) {
        classNames.push('in-range');
      }
      if (sameDay(date, lo) || sameDay(date, hi)) {
        classNames.push('edge');
        if (sameDay(lo, hi)) {
          classNames.push('solo');
        } else if (sameDay(date, lo)) {
          classNames.push('start');
        } else {
          classNames.push('end');
        }
      }
    } else if (sameDay(date, rangeStart)) {
      classNames.push('edge', 'solo');
    }

    cells.push({
      key: `${year}-${monthIndex}-${day}`,
      day,
      date,
      className: classNames.join(' '),
    });
  }

  return cells;
};

const fieldClass = 'flex flex-1 cursor-pointer flex-col gap-px rounded-[9px] border border-ink-200 bg-white px-[11px] py-[7px]';

const fieldActiveClass = 'border-ar-blue shadow-[0_0_0_3px_var(--ar-blue-soft)]';

const navClass =
  'grid size-[26px] place-items-center rounded-[7px] border border-ink-200 bg-white text-ink-600 hover:border-ar-blue hover:text-ar-blue disabled:cursor-not-allowed disabled:opacity-50';

const btnClass =
  'cursor-pointer rounded-lg border-0 bg-brand-600 px-3.5 py-[7px] text-[12.5px] font-semibold text-white-white disabled:cursor-not-allowed disabled:bg-ink-200 disabled:text-ink-500';

const btnGhostClass = 'border border-ink-200 bg-white text-ink-700 hover:border-ink-300';

export const DateRangeCustomPanel: FC<DateRangeCustomPanelProps> = ({ initialFrom, initialTo, onApply, onCancel }) => {
  const { t, i18n } = useTranslation('performanceAudience');
  const today = useMemo(() => startOfDay(new Date()), []);

  const seededStart = useMemo(() => {
    if (initialFrom) {
      return parseIsoDate(initialFrom);
    }
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return start;
  }, [initialFrom, today]);

  const seededEnd = useMemo(() => {
    if (initialTo) {
      return parseIsoDate(initialTo);
    }
    return new Date(today);
  }, [initialTo, today]);

  const [rangeStart, setRangeStart] = useState<Date | null>(seededStart);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(seededEnd);
  const [hoverDay, setHoverDay] = useState<Date | null>(null);
  const [viewMonth, setViewMonth] = useState(() => new Date(seededStart.getFullYear(), seededStart.getMonth() - 1, 1));

  useEffect(() => {
    setRangeStart(seededStart);
    setRangeEnd(seededEnd);
    setViewMonth(new Date(seededStart.getFullYear(), seededStart.getMonth() - 1, 1));
  }, [seededStart, seededEnd]);

  const monthTitles = useMemo(() => {
    const second = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);
    const formatter = new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' });
    return [formatter.format(viewMonth), formatter.format(second)];
  }, [viewMonth, i18n.language]);

  const months = useMemo(() => [viewMonth, new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1)], [viewMonth]);

  const fromIso = rangeStart ? formatIsoDate(rangeStart) : '';
  const toIso = rangeEnd ? formatIsoDate(rangeEnd) : '';
  const isComplete = Boolean(rangeStart && rangeEnd);
  const isValid = isComplete && isValidCustomDateRange(fromIso, toIso);
  const dayCount = isComplete ? inclusiveDayCount(rangeStart!, rangeEnd!) : 0;
  const exceedsMax = isComplete && dayCount > MAX_CUSTOM_DATE_RANGE_DAYS;

  const hint = !rangeStart
    ? t('dateCustom.hintStart')
    : !rangeEnd
      ? t('dateCustom.hintEnd')
      : exceedsMax
        ? t('dateCustom.hintMaxDays', { max: MAX_CUSTOM_DATE_RANGE_DAYS })
        : t('dateCustom.hintSelected', { count: dayCount });

  const handleDayClick = (date: Date) => {
    if (date > today) {
      return;
    }

    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(date);
      setRangeEnd(null);
      setHoverDay(null);
      return;
    }

    if (date < rangeStart) {
      setRangeEnd(rangeStart);
      setRangeStart(date);
      return;
    }

    setRangeEnd(date);
  };

  const canGoNext = useMemo(() => {
    const next = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);
    return next <= new Date(today.getFullYear(), today.getMonth(), 1);
  }, [viewMonth, today]);

  const pickingEnd = Boolean(rangeStart && !rangeEnd);

  return (
    <div className='flex w-[480px] shrink-0 flex-col gap-3 px-2.5 pb-2.5 pt-2'>
      <div className='flex items-center gap-2.5'>
        <label className={twMerge(fieldClass, pickingEnd && fieldActiveClass)}>
          <span className='text-[10.5px] font-semibold uppercase tracking-[0.07em] text-ink-500'>{t('dateCustom.from')}</span>
          <span className='text-[13px] font-semibold tabular-nums text-ink-900'>{rangeStart ? formatDisplayDate(rangeStart, i18n.language) : '—'}</span>
        </label>
        <span className='text-sm text-ink-400'>→</span>
        <label className={twMerge(fieldClass, pickingEnd && fieldActiveClass)}>
          <span className='text-[10.5px] font-semibold uppercase tracking-[0.07em] text-ink-500'>{t('dateCustom.to')}</span>
          <span className='text-[13px] font-semibold tabular-nums text-ink-900'>{rangeEnd ? formatDisplayDate(rangeEnd, i18n.language) : '—'}</span>
        </label>
      </div>

      <div className='flex items-center justify-between px-0.5'>
        <button
          type='button'
          className={navClass}
          aria-label={t('dateCustom.prevMonth')}
          onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden>
            <path d='M15 18l-6-6 6-6' />
          </svg>
        </button>
        <div className='flex flex-1 justify-around text-center text-[13px] font-semibold text-ink-900'>
          <span>{monthTitles[0]}</span>
          <span>{monthTitles[1]}</span>
        </div>
        <button
          type='button'
          className={navClass}
          aria-label={t('dateCustom.nextMonth')}
          disabled={!canGoNext}
          onClick={() => {
            if (!canGoNext) {
              return;
            }
            setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
          }}
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden>
            <path d='M9 18l6-6-6-6' />
          </svg>
        </button>
      </div>

      <div className='grid grid-cols-2 gap-5'>
        {months.map((month) => {
          const cells = buildMonthCells(month, today, rangeStart, rangeEnd, hoverDay);
          return (
            <div key={`${month.getFullYear()}-${month.getMonth()}`} className='grid grid-cols-7 gap-0.5'>
              {DOW_KEYS.map((key) => (
                <div key={key} className='px-0 pb-0.5 pt-1 text-center text-[10px] font-semibold uppercase tracking-[0.04em] text-ink-400'>
                  {t(`dateCustom.dow.${key}`)}
                </div>
              ))}
              {cells.map((cell) => (
                <div
                  key={cell.key}
                  className={cell.className}
                  onClick={() => cell.date && handleDayClick(cell.date)}
                  onMouseEnter={() => {
                    if (!cell.date || !rangeStart || rangeEnd || cell.date > today) {
                      return;
                    }
                    setHoverDay(cell.date);
                  }}
                  onMouseLeave={() => setHoverDay(null)}
                >
                  {cell.day}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className='flex items-center justify-between gap-3 border-t border-ink-100 pt-2.5'>
        <span className='text-xs text-ink-500'>{hint}</span>
        <div className='flex gap-2'>
          <button type='button' className={twMerge(btnClass, btnGhostClass)} onClick={onCancel}>
            {t('dateCustom.cancel')}
          </button>
          <Button
            type='button'
            intent='primary'
            className={btnClass}
            disabled={!isValid}
            onClick={() => {
              if (!isValid || !rangeStart || !rangeEnd) {
                return;
              }
              onApply({ from: formatIsoDate(rangeStart), to: formatIsoDate(rangeEnd) });
            }}
          >
            {t('dateCustom.apply')}
          </Button>
        </div>
      </div>
    </div>
  );
};
